import axios from 'axios';
import config from './config';

const api = axios.create({
  baseURL: config.API_URL,
  timeout: 10000,
  withCredentials: true,
});

// ============================================
// AUTHENTICATION
// ============================================

// Check if backend is already authenticated
export const checkAuthStatus = async () => {
  const response = await api.get('/api/auth/status');
  return response.data;
};

// Get access token (refreshes automatically if needed)
export const getAccessToken = async () => {
  const response = await api.get('/api/auth/token');
  return response.data;
};

// Force token refresh
export const forceRefreshToken = async () => {
  const response = await api.post('/api/auth/refresh');
  return response.data;
};

// ============================================
// SEARCH
// ============================================

export const searchTracks = async (query, offset = 0, signal) => {
  const response = await api.get('/api/search', { params: { q: query, offset }, signal });
  return response.data;
};

export const searchArtists = async (query) => {
  const response = await api.get('/api/artists/search', { params: { q: query } });
  return response.data.artists;
};

// ============================================
// PLAYLISTS
// ============================================

export const getUserPlaylists = async () => {
  const response = await api.get('/api/playlists');
  return response.data.playlists;
};

export const getPlaylistTracks = async (playlistId) => {
  const response = await api.get(`/api/playlists/${playlistId}/items`);
  return response.data.tracks;
};

// ============================================
// GENRE
// ============================================

export const getGenreTracks = async (genre) => {
  const response = await api.get(`/api/genre/${genre}`);
  return response.data.tracks;
};

// ============================================
// ARTIST RADIO
// ============================================

export const getArtistRadio = async (artistId) => {
  const response = await api.get(`/api/artist/${artistId}/radio`);
  return response.data.tracks;
};

// ============================================
// QUEUE MANAGEMENT
// ============================================

export const getQueue = async () => {
  const response = await api.get('/api/queue');
  return response.data;
};

export const addToQueue = async (track) => {
  const response = await api.post('/api/queue/add', { track });
  return response.data;
};

export const removeFromQueue = async (index, queueItemId) => {
  const response = await api.delete(`/api/queue/${index}`, { params: { queueItemId } });
  return response.data;
};

// Remove first item from queue (when track finishes playing)
export const shiftQueue = async () => {
  const response = await api.post('/api/queue/shift');
  return response.data;
};

export const clearQueue = async () => {
  const response = await api.delete('/api/queue/clear');
  return response.data;
};

// ============================================
// BACKGROUND PLAYLIST
// ============================================

// Set a playlist as background music (shuffled)
export const setBackgroundPlaylist = async (playlistId, playlistName) => {
  const response = await api.post('/api/background/set', { playlistId, playlistName });
  return response.data;
};

// Get background playlist status
export const getBackgroundStatus = async () => {
  const response = await api.get('/api/background/status');
  return response.data;
};

// Get next track to play (priority: queue > background)
export const getNextTrack = async () => {
  const response = await api.get('/api/next-track');
  return response.data;
};

// Advance to next track (call after track finishes)
export const advanceTrack = async () => {
  const response = await api.post('/api/advance-track');
  return response.data;
};

// Stop background playlist
export const stopBackgroundPlaylist = async () => {
  const response = await api.post('/api/background/stop');
  return response.data;
};

// Reshuffle background playlist
export const reshuffleBackground = async () => {
  const response = await api.post('/api/background/reshuffle');
  return response.data;
};

// ============================================
// ADMIN
// ============================================

// Clear all queues (priority + background)
export const adminClearAll = async () => {
  const response = await api.post('/api/admin/clear-all');
  return response.data;
};

export const getAdminSession = async () => {
  const response = await api.get('/api/admin/session');
  return response.data;
};

export const adminLogin = async (password) => {
  const response = await api.post('/api/admin/login', { password });
  return response.data;
};

export const adminLogout = async () => {
  const response = await api.post('/api/admin/logout');
  return response.data;
};

export const getAdminStatus = async () => {
  const response = await api.get('/api/admin/status');
  return response.data;
};

export const getSpotifyAuthorizationUrl = async () => {
  const response = await api.get('/api/admin/spotify/authorize');
  return response.data.url;
};

export const sendPlayerCommand = async (command) => {
  const response = await api.post('/api/admin/player-command', { command });
  return response.data;
};

export const reportPlayerStatus = async (status) => {
  await api.post('/api/player/status', status);
};

export const getPlayerCommand = async () => {
  const response = await api.get('/api/player/command');
  return response.data.command;
};

// ============================================
// HEALTH CHECK
// ============================================

export const checkHealth = async () => {
  const response = await api.get('/api/health');
  return response.data;
};

export default api;
